<template>
  <div class="offer-item">
    <a :href="viewModel.hotelDescriptionUrl" target="_blank" class="offer-item__image"
       :style="promotionImageStyle">
      <svg-icon v-if="!viewModel.hotelImageUrl" width="72" height="72" icon="no-photo"/>
    </a>
    <div>
      <div class="offer-item__rating">
        <template v-if="!!viewModel.hotelRating">
          <svg-icon v-for="index in viewModel.hotelRating"
                    :key="index"
                    icon="star"
                    width="16"
                    height="16"
          />
        </template>
        <div v-else>
          {{ viewModel.hotelCategoryName }}
        </div>
      </div>
      <div class="offer-item__hotel-name text-overflow-ellipsis"
           v-tippy="$tippyOptions(viewModel.hotelName, {disableIfNotCropped: true})">
        <a :href="viewModel.hotelDescriptionUrl">{{ viewModel.hotelName }}</a>
      </div>
    </div>
    <div class="offer-item__hotel-location">
      <svg-icon icon="location-marker"/>
      <span>
        <a :href="viewModel.hotelCountryUrl">{{ country.name + "," }}</a>
        <a :href="viewModel.hotelLocationUrl">{{ location.name }}</a>
      </span>
    </div>
    <div class="offer-item__date">
      <svg-icon icon="calendar"/>
      <span>
        <span>{{ $formatDateShortDDMM(viewModel.startDate) }}</span>
        &mdash;
        <span>{{ $formatDateShortDDMM(viewModel.endDate) }}</span>        
      </span>
      <Badge class="offer-item__badge-duration" :text="viewModel.stayingDuration"/>
    </div>
    <div class="offer-item__price">
      <svg-icon icon="avatar" class="offer-item__icon-price"/>
      <a role="button" :href="viewModel.constructBookingUrl" target="_blank" dir="ltr"
         class="offer-item__current-price">
        {{ $formatPrice0(viewModel.currentPrice) }} {{ currencySymbolOrCode }}
      </a>
      <div class="offer-item__price-discount">
        <span dir="ltr" v-if="hasDiscountOrSpecialOffers" class="offer-item__old-price">
          {{ $formatPrice0(viewModel.oldPrice) }} {{ currencySymbolOrCode }}
        </span>
        <span :ref="setSpoTarget">
          <Badge
              v-if="discountText"
              class="offer-item__badge-price"
              :text="discountText"
          />
        </span>
        <offer-item-spo v-if="hasSpoInfo"
                        class="display-none"
                        :target-element="spoTargetEl"
                        :enabled="hasSpoInfo"
                        :get-spo-info-fn="() => buildSpoInfo(viewModel)"
                        :hotel-promotions="viewModel.hotelPromotions"/>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Badge from "@/common/controls/BadgeComponent2.vue";
import SvgIcon from "@/common/controls/SvgIconControl.vue";
import OfferItemSpo from "./OfferItemSpoComponent.vue";

export default {
  components: {
    Badge,
    SvgIcon,
    OfferItemSpo,
  },
  props: {
    viewModel: Object,
    country: Object,
    location: Object,
    currency: Object,
    isAuthorized: Boolean,
  },
  computed: {
    currencySymbolOrCode: function () {
      if (this.currency.symbol) {
        return this.currency.symbol;
      }
      if (this.currency.iso) {
        this.currency.iso;
      }
      return this.currency.code;
    },
    hasDiscountOrSpecialOffers: function () {
      const _a = this.viewModel, discountPercent = _a.discountPercent, _b = _a.specialOffers,
          specialOffers = _b === void 0 ? [] : _b;
      return discountPercent > 0 || specialOffers.length > 0;
    },
    hasSpoInfo() {
      const promotions = this.viewModel?.hotelPromotions ?? [];
      return !!this.viewModel?.packageSpo || promotions.length > 0;
    },
    promotionImageStyle: function () {
      if (this.viewModel.hotelImageUrl) {
        return {
          "background-image": `url(${this.viewModel.hotelImageUrl})`,
          'background-size': "cover",
        };
      }
      return {};
    },
    discountText() {
      const {discountPercent} = this.viewModel;
      return discountPercent !== 0
          ? `${discountPercent > 0 ? "-" : ""}${discountPercent}%`
          : "";
    },
  },
  data() {
    return {
      spoTargetEl: null,
    };
  },
  methods: {
    setSpoTarget(el) {
      this.spoTargetEl = el;
    },
    buildSpoInfo(item) {
      const ret = {
        agencySpecific: null,
        promotions: [],
        expirationDateText: null,
        expiresInText: null,
        userAuthorized: this.isAuthorized,
        noPackage: false,
        backgroundColor: null,
        name: null,
      }

      if (!item || !item.packageSpo) {
        ret.noPackage = true;
        return ret;
      }

      const {
        minPrice,
        maxPrice,
        packageSpoExpiresInText,
        packageSpoExpirationDate
      } = item;

      const packageSpo = item.packageSpo ?? {};
      const {agencyFee} = packageSpo;

      ret.name = packageSpo.name;
      ret.backgroundColor = packageSpo.colorCode;

      ret.agencySpecific = item.agencySpecific;

      ret.expiresInText = packageSpoExpiresInText;

      if (!!agencyFee) {
        const {
          reductionPercent,
          agencyFeeChangePercent,
          agencyBonusAmount,
          agencyEmployeeBonusAmount,
        } = agencyFee;

        if (reductionPercent > 0) {
          let reductionPercentFormat = reductionPercent >= 0.01 ?
              Math.trunc(reductionPercent * 100) :
              (reductionPercent * 100).toFixed(1);

          ret.promotions.push(
              this.$fmt(
                  this.$res.Views.PackageSearch_Promotion_ReductionIncludedinPrice,
                  reductionPercentFormat + '%'));

        }

        if (!!agencyFeeChangePercent) {
          if (agencyFeeChangePercent > 0) {
            ret.promotions.push(
                this.$fmt(
                    this.$res.Views.PackageSearch_IncreasedAgencyFee,
                    `${agencyFeeChangePercent * 100}%`));
          } else if (agencyFeeChangePercent != 0) {
            ret.promotions.push(
                this.$fmt(
                    this.$res.Views.PackageSearch_FixedAgencyFee,
                    `${agencyFeeChangePercent * 100}%`));
          }
        }

        if (agencyBonusAmount > 0) {
          ret.promotions.push(
              this.$fmt(
                  this.$res.Views.PackageSearch_AgencyBonusFormat,
                  `${this.$formatPrice0(Math.round(agencyBonusAmount))} ${this.currencySymbolOrCode}`)
          );
        }

        if (agencyEmployeeBonusAmount > 0) {
          ret.promotions.push(
              this.$fmt(
                  this.$res.Views.PackageSearch_AgencyEmployeeBonusFormat,
                  `${this.$formatPrice0(Math.round(agencyBonusAmount))} ${this.currencySymbolOrCode}`)
          );
        }
      }

      let priceLimitText = null;

      if (!!minPrice || !!maxPrice) {
        if (minPrice == null) {
          priceLimitText = this.$fmt(
              this.$res.Views.PackageSearch_Promotion_ValidForTourWithPriceMaxFormat,
              this.$formatPrice0(maxPrice),
              this.currencySymbolOrCode
          );
        } else if (maxPrice == null) {
          priceLimitText = this.$fmt(
              this.$res.Views.PackageSearch_Promotion_ValidForTourWithPriceMinFormat,
              this.$formatPrice0(minPrice),
              this.currencySymbolOrCode
          );
        } else {
          priceLimitText = this.$fmt(
              this.$res.Views.PackageSearch_Promotion_ValidForTourWithPriceMinMaxFormat,
              this.$formatPrice0(minPrice),
              this.$formatPrice0(maxPrice),
              this.currencyDisplayText(item.TourCurrencyId));
        }
      }

      if (!!packageSpoExpirationDate) {
        let bookingUntilText = this.$fmt(this.$res.Views.PackageSearch_Promotion_ValidForBookingTillFormat,
            [`${this.$formatDateShort(packageSpoExpirationDate)} ${this.$formatTime(packageSpoExpirationDate)}`]);

        if (!!priceLimitText) {
          bookingUntilText += ", " + priceLimitText;
        }

        ret.expirationDateText = bookingUntilText;
      } else if (!!priceLimitText) {
        ret.expirationDateText = this.$res.Views.PackageSearch_Promotion_ValidForTourWithPriceBegin + ' ' + priceLimitText;
      }

      return ret;
    },
  },
}
</script>

<style lang="scss" scoped>


.offer-item {
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  padding: 12px;
  height: 313px;

  color: var(--second-10);
  background-color: #fff;
  //flex: 1 0 30%;
  border-radius: var(--border-radius-lg);

  * {
    color: inherit;
  }
}

.offer-item__hotel-name {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
}

.offer-item__hotel-location {
  font-size: 13px;
  display: flex;
  flex-flow: row nowrap;
  gap: 8px;
  align-items: center;
}

.offer-item__date {
  display: flex;
  gap: 8px;
  align-items: center;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: var(--second-10);

  svg {
  }
}

.offer-item__price {
  display: flex;
  gap: 8px;
  align-items: center;
}

.offer-item__price-discount {
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
}

.offer-item__current-price {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: var(--main-10);
}

.offer-item__old-price {
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: var(--second-30);
  text-decoration: line-through;
}

.offer-item__badge-duration {
  display: inline-block;
  padding: 2px 8px;

  font-weight: 400;
  font-size: 11px;
  line-height: 16px;
  height: 20px; // todo 12325 overrides badge-component style.
  color: var(--second-10);
  background: var(--main-40);
  border-radius: var(--border-radius-sm);
}

.offer-item__badge-price {
  display: inline-flex;
  align-items: center;
  background: var(--main-20);
  color: var(--background);

  &:hover {
    background: var(--main-10);
  }
}

.offer-item__icon-price {
  color: var(--main-10);
}

.offer-item__rating {
  display: flex;
  gap: 4px;
  align-items: center;
  color: var(--system-10);
}

.offer-item__image {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  border-radius: var(--border-radius-md);
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-color: var(--main-50);
  margin-bottom: 5px;
  color: var(--main-30);
}

.offer-item__discount-tooltip {
  left: -30px !important;
  opacity: 1 !important;
  background: transparent !important;
  border: none !important;
  right: 0;
}

.offer-item__special-offers-item {
  color: white;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 4px;
}

.offer-item__special-offers-list {
  margin: 0;
  padding: 0;
  list-style-type: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
</style>



